import { create } from 'zustand'
import type { CrowdTask, CrowdSubmission, AdminTaskDetail, Category, CrowdProduct, CrowdMerchant, AdminWorker, AdminWorkerDetail, LeaderboardEntry, CrowdTaskItemUpdate } from '../types'
import * as adminApi from '../api/admin'

const STORAGE_KEY = 'crowd_admin_key'

interface AdminState {
  apiKey: string | null
  tasks: CrowdTask[]
  totalTasks: number
  taskDetail: AdminTaskDetail | null
  pendingSubmissions: CrowdSubmission[]
  categories: Category[]
  crowdProducts: CrowdProduct[]
  totalCrowdProducts: number
  crowdMerchants: CrowdMerchant[]
  totalCrowdMerchants: number
  stats: adminApi.AdminStats | null
  workers: AdminWorker[]
  totalWorkers: number
  workerDetail: AdminWorkerDetail | null
  leaderboard: LeaderboardEntry[]
  isLoading: boolean
  error: string | null

  setApiKey: (key: string) => void
  clearApiKey: () => void
  fetchTasks: () => Promise<void>
  createTask: (data: adminApi.AdminTaskCreate) => Promise<CrowdTask>
  updateTask: (taskId: string, data: adminApi.AdminTaskUpdate) => Promise<CrowdTask>
  publishTask: (taskId: string) => Promise<void>
  fetchTaskDetail: (taskId: string) => Promise<void>
  fetchCategories: () => Promise<void>
  fetchPendingSubmissions: () => Promise<void>
  verifySubmission: (id: string, status: 'verified' | 'rejected', comment?: string, auto_publish?: boolean) => Promise<void>
  fetchCrowdProducts: (params?: adminApi.CrowdProductsParams, append?: boolean) => Promise<void>
  publishProduct: (productId: string) => Promise<void>
  archiveProduct: (productId: string) => Promise<void>
  bulkPublishProducts: (productIds: string[]) => Promise<void>
  fetchCrowdMerchants: (params?: adminApi.CrowdMerchantsParams, append?: boolean) => Promise<void>
  verifyCrowdMerchant: (merchantId: string) => Promise<void>
  fetchStats: () => Promise<void>
  fetchWorkers: (params?: adminApi.WorkersParams, append?: boolean) => Promise<void>
  fetchWorkerDetail: (workerId: string) => Promise<void>
  fetchLeaderboard: () => Promise<void>
  uploadTaskImage: (file: File) => Promise<string>
  uploadTaskGalleryImage: (taskId: string, file: File) => Promise<void>
  deleteTaskGalleryImage: (taskId: string, imageId: string) => Promise<void>
  uploadTaskItemImage: (taskId: string, itemId: string, file: File) => Promise<void>
  uploadTaskItemGalleryImage: (taskId: string, itemId: string, file: File) => Promise<void>
  deleteTaskItemGalleryImage: (taskId: string, itemId: string, imageId: string) => Promise<void>
  addTaskItem: (taskId: string, data: adminApi.AdminTaskItemCreate) => Promise<void>
  updateTaskItem: (taskId: string, itemId: string, data: CrowdTaskItemUpdate) => Promise<void>
  deleteTaskItem: (taskId: string, itemId: string) => Promise<void>
}

// Init key from localStorage
const storedKey = localStorage.getItem(STORAGE_KEY)
if (storedKey) adminApi.setAdminApiKey(storedKey)

export const useAdminStore = create<AdminState>((set, get) => ({
  apiKey: storedKey,
  tasks: [],
  totalTasks: 0,
  taskDetail: null,
  pendingSubmissions: [],
  categories: [],
  crowdProducts: [],
  totalCrowdProducts: 0,
  crowdMerchants: [],
  totalCrowdMerchants: 0,
  stats: null,
  workers: [],
  totalWorkers: 0,
  workerDetail: null,
  leaderboard: [],
  isLoading: false,
  error: null,

  setApiKey: (key) => {
    localStorage.setItem(STORAGE_KEY, key)
    adminApi.setAdminApiKey(key)
    set({ apiKey: key })
  },

  clearApiKey: () => {
    localStorage.removeItem(STORAGE_KEY)
    adminApi.setAdminApiKey('')
    set({ apiKey: null, tasks: [], taskDetail: null, pendingSubmissions: [], stats: null, categories: [], crowdProducts: [], crowdMerchants: [] })
  },

  fetchTasks: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await adminApi.listAllTasks()
      set({ tasks: data.items, totalTasks: data.total, isLoading: false })
    } catch (e: unknown) {
      set({ error: e instanceof Error ? e.message : 'Failed to load tasks', isLoading: false })
    }
  },

  createTask: async (data) => {
    const task = await adminApi.createTask(data)
    set((state) => ({ tasks: [task, ...state.tasks], totalTasks: state.totalTasks + 1 }))
    return task
  },

  updateTask: async (taskId, data) => {
    const task = await adminApi.updateTask(taskId, data)
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === taskId ? task : t)),
      taskDetail: state.taskDetail?.id === taskId ? { ...state.taskDetail, ...task } : state.taskDetail,
    }))
    return task
  },

  publishTask: async (taskId) => {
    const updated = await adminApi.publishTask(taskId)
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === taskId ? updated : t)),
    }))
  },

  fetchTaskDetail: async (taskId) => {
    set({ isLoading: true, error: null })
    try {
      const detail = await adminApi.getTaskDetail(taskId)
      set({ taskDetail: detail, isLoading: false })
    } catch (e: unknown) {
      set({ error: e instanceof Error ? e.message : 'Failed to load task', isLoading: false })
    }
  },

  fetchCategories: async () => {
    try {
      const categories = await adminApi.listCategories()
      set({ categories })
    } catch {
      // Non-fatal
    }
  },

  fetchPendingSubmissions: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await adminApi.listPendingSubmissions()
      set({ pendingSubmissions: data, isLoading: false })
    } catch (e: unknown) {
      set({ error: e instanceof Error ? e.message : 'Failed to load submissions', isLoading: false })
    }
  },

  verifySubmission: async (id, status, comment, auto_publish = false) => {
    await adminApi.verifySubmission(id, status, comment, auto_publish)
    set((state) => ({
      pendingSubmissions: state.pendingSubmissions.filter((s) => s.id !== id),
    }))
  },

  fetchCrowdProducts: async (params, append = false) => {
    set({ isLoading: true, error: null })
    try {
      const data = await adminApi.listCrowdProducts(params)
      set((state) => ({
        crowdProducts: append ? [...state.crowdProducts, ...data.items] : data.items,
        totalCrowdProducts: data.total,
        isLoading: false,
      }))
    } catch (e: unknown) {
      set({ error: e instanceof Error ? e.message : 'Failed to load products', isLoading: false })
    }
  },

  publishProduct: async (productId) => {
    await adminApi.publishProduct(productId)
    set((state) => ({
      crowdProducts: state.crowdProducts.map((p) =>
        p.id === productId ? { ...p, status: 'published' } : p
      ),
    }))
  },

  archiveProduct: async (productId) => {
    await adminApi.archiveProduct(productId)
    set((state) => ({
      crowdProducts: state.crowdProducts.map((p) =>
        p.id === productId ? { ...p, status: 'archived' } : p
      ),
    }))
  },

  bulkPublishProducts: async (productIds) => {
    await adminApi.bulkPublishProducts(productIds)
    const idSet = new Set(productIds)
    set((state) => ({
      crowdProducts: state.crowdProducts.map((p) =>
        idSet.has(p.id) ? { ...p, status: 'published' } : p
      ),
    }))
  },

  fetchCrowdMerchants: async (params, append = false) => {
    set({ isLoading: true, error: null })
    try {
      const data = await adminApi.listCrowdMerchants(params)
      set((state) => ({
        crowdMerchants: append ? [...state.crowdMerchants, ...data.items] : data.items,
        totalCrowdMerchants: data.total,
        isLoading: false,
      }))
    } catch (e: unknown) {
      set({ error: e instanceof Error ? e.message : 'Failed to load merchants', isLoading: false })
    }
  },

  verifyCrowdMerchant: async (merchantId) => {
    await adminApi.verifyCrowdMerchant(merchantId)
    set((state) => ({
      crowdMerchants: state.crowdMerchants.map((m) =>
        m.id === merchantId ? { ...m, is_crowd_verified: true } : m
      ),
    }))
  },

  fetchStats: async () => {
    try {
      const stats = await adminApi.getStats()
      set({ stats })
    } catch {
      // Non-fatal — stats just won't show
    }
  },

  fetchWorkers: async (params, append = false) => {
    set({ isLoading: true, error: null })
    try {
      const data = await adminApi.listWorkers(params)
      set((state) => ({
        workers: append ? [...state.workers, ...data.items] : data.items,
        totalWorkers: data.total,
        isLoading: false,
      }))
    } catch (e: unknown) {
      set({ error: e instanceof Error ? e.message : 'Failed to load workers', isLoading: false })
    }
  },

  fetchWorkerDetail: async (workerId) => {
    set({ isLoading: true, error: null })
    try {
      const detail = await adminApi.getWorkerDetail(workerId)
      set({ workerDetail: detail, isLoading: false })
    } catch (e: unknown) {
      set({ error: e instanceof Error ? e.message : 'Failed to load worker', isLoading: false })
    }
  },

  fetchLeaderboard: async () => {
    try {
      const leaderboard = await adminApi.getLeaderboard()
      set({ leaderboard })
    } catch {
      // Non-fatal
    }
  },

  uploadTaskImage: async (file) => {
    const { url } = await adminApi.uploadTaskImage(file)
    return url
  },

  uploadTaskGalleryImage: async (taskId, file) => {
    const updatedTask = await adminApi.uploadTaskGalleryImage(taskId, file)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, gallery_images: updatedTask.gallery_images }
        : state.taskDetail,
    }))
  },

  deleteTaskGalleryImage: async (taskId, imageId) => {
    const updatedTask = await adminApi.deleteTaskGalleryImage(taskId, imageId)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, gallery_images: updatedTask.gallery_images }
        : state.taskDetail,
    }))
  },

  uploadTaskItemImage: async (taskId, itemId, file) => {
    const updatedTask = await adminApi.uploadTaskItemImage(taskId, itemId, file)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, items: updatedTask.items }
        : state.taskDetail,
    }))
  },

  uploadTaskItemGalleryImage: async (taskId, itemId, file) => {
    const updatedTask = await adminApi.uploadTaskItemGalleryImage(taskId, itemId, file)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, items: updatedTask.items }
        : state.taskDetail,
    }))
  },

  deleteTaskItemGalleryImage: async (taskId, itemId, imageId) => {
    const updatedTask = await adminApi.deleteTaskItemGalleryImage(taskId, itemId, imageId)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, items: updatedTask.items }
        : state.taskDetail,
    }))
  },

  addTaskItem: async (taskId, data) => {
    const updatedTask = await adminApi.addTaskItem(taskId, data)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, items: updatedTask.items }
        : state.taskDetail,
    }))
  },

  updateTaskItem: async (taskId, itemId, data) => {
    const updatedTask = await adminApi.updateTaskItem(taskId, itemId, data)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, items: updatedTask.items }
        : state.taskDetail,
    }))
  },

  deleteTaskItem: async (taskId, itemId) => {
    const updatedTask = await adminApi.deleteTaskItem(taskId, itemId)
    set((state) => ({
      taskDetail: state.taskDetail?.id === taskId
        ? { ...state.taskDetail, items: updatedTask.items }
        : state.taskDetail,
    }))
  },
}))
